import { useEffect, useState, useRef, useCallback } from 'react';

const ZERO = 0;

interface FitScreenOptions {
  watchWindowResize?: boolean;
  bottomDistance?: number;
  safeValueReduction?: number;
  defaultValue?: number;
  direction?: 'top' | 'right';
}

export function useFitScreen<El>({
  bottomDistance,
  watchWindowResize = true,
  defaultValue = ZERO,
  direction = 'top',
}: FitScreenOptions = {}): [number, (node: (El & HTMLElement) | null) => void, () => number] {
  const ref = useRef<(El & HTMLElement) | null>(null);
  const [containerMaxHeight, setContainerMaxHeight] = useState(defaultValue);

  const calculateHeight = useCallback(() => {
    if (!ref.current) return defaultValue;

    const reduceMeasure = (bottomDistance ?? 0) + ref.current.getBoundingClientRect()[direction];

    const maxHeight = window.innerHeight - reduceMeasure;

    setContainerMaxHeight(maxHeight);

    return maxHeight;
  }, [bottomDistance, ref, direction, defaultValue]);

  useEffect(() => {
    if (!watchWindowResize) return;

    window.addEventListener('resize', calculateHeight);

    return () => window.removeEventListener('resize', calculateHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRef = useCallback(
    (node: (El & HTMLElement) | null) => {
      if (!node) return;

      ref.current = node;

      calculateHeight();
    },
    [calculateHeight],
  );

  return [containerMaxHeight, setRef, calculateHeight];
}
